import type { RefObject } from "react";
import { useCallback, useEffect, useRef } from "react";
import useSWR from "swr";
import { audienceImportSessionsApi } from "@/lib/api";
import type { AudienceCreationFlowSnapshot } from "@digital-twins/shared-types";
import type { ImportSessionUpdatedEvent } from "@/lib/services/websocket";
import type { ImportSessionEventHandler } from "@/types/components/audiences/import-wizard-context";
import { useImportSessionWebsocket } from "./useImportSessionWebsocket";
import { getAudienceImportFlowSnapshotKey } from "../lib/cache-keys";
import { shouldRevalidateFlowSnapshotForImportSessionUpdate } from "../lib/import-session-update-events";
import {
  getRouteAckFromImportSessionEvent,
  preferNewerFlowSnapshot,
} from "../lib/import-session-deltas";
import { useRealtimeRepair } from "@/lib/realtime/useRealtimeRepair";
import { incrementPollWonRepair } from "@/lib/realtime/pollWonMetrics";
import { legacyRefreshInterval } from "@/lib/realtime/realtimeKillSwitch";

function shouldPoll(snapshot: AudienceCreationFlowSnapshot | undefined) {
  if (!snapshot) {
    return false;
  }
  if (snapshot.preflightStatus === "pending") {
    return true;
  }
  if (
    snapshot.machineState === "creating" ||
    snapshot.wizardPhase === "analyze_running" ||
    snapshot.wizardPhase === "preview_hydrating" ||
    snapshot.wizardPhase === "preview_preparing" ||
    snapshot.wizardPhase === "create_running"
  ) {
    return true;
  }
  if (snapshot.hasPreflightData === false) {
    const phase = snapshot.wizardPhase;
    if (phase === "identity_review" || phase === "segmentation") {
      return true;
    }
  }
  return false;
}

export interface UseFlowSnapshotOptions {
  /** Ref updated synchronously by mutation callers; avoids one-tick race with websocket echo. */
  lastMutationRequestIdRef?: RefObject<string | null>;
  /** @deprecated Use lastMutationRequestIdRef for layout/wizard usage. */
  lastMutationRequestId?: string | null;
}

/**
 * Lightweight hook to fetch the canonical flow snapshot for a session.
 * Owns the single websocket subscription for the import wizard; projection hooks
 * register handlers via registerImportSessionEventHandler instead of attaching
 * their own websocket listeners.
 */
export function useFlowSnapshot(
  sessionId: string,
  options: UseFlowSnapshotOptions = {},
) {
  const { lastMutationRequestIdRef, lastMutationRequestId } = options;
  const handlersRef = useRef<Set<ImportSessionEventHandler>>(new Set());
  const registerHandler = useCallback((handler: ImportSessionEventHandler) => {
    handlersRef.current.add(handler);
    return () => {
      handlersRef.current.delete(handler);
    };
  }, []);

  const masterHandlerRef = useRef<
    (event: ImportSessionUpdatedEvent) => void | Promise<void>
  >(null!);

  const key = getAudienceImportFlowSnapshotKey(sessionId);
  const query = useSWR<AudienceCreationFlowSnapshot | null>(
    key,
    sessionId
      ? async (_key: unknown) => {
          try {
            // Do not pass SWR's abort signal. Cancelling a credentialed
            // cross-origin XHR is reported by Chrome as a CORS failure
            // (missing Access-Control-Allow-Origin) even when the retry succeeds.
            return await audienceImportSessionsApi.getFlowSnapshot(sessionId);
          } catch (err: unknown) {
            if (
              err &&
              typeof err === "object" &&
              "response" in err &&
              (err as { response?: { status?: number } }).response?.status ===
                404
            ) {
              return null;
            }
            throw err;
          }
        }
      : null,
    {
      refreshInterval: legacyRefreshInterval("IMPORT", 1500),
      revalidateOnFocus: false,
      dedupingInterval: 5000,
      errorRetryCount: 5,
      errorRetryInterval: 750,
    },
  );

  const { isConnected: isRealtimeConnected } = useImportSessionWebsocket(
    sessionId,
    (e) => void masterHandlerRef.current(e),
    {
      latestMachineVersion: query.data?.machineVersion,
      lastMutationRequestIdRef,
      lastMutationRequestId,
    },
  );

  const mutateRef = useRef(query.mutate);
  mutateRef.current = query.mutate;

  const revalidateSnapshot = useCallback(async () => {
    if (!sessionId) return;
    await mutateRef.current(
      async (current) =>
        preferNewerFlowSnapshot(
          current as AudienceCreationFlowSnapshot | null | undefined,
          await audienceImportSessionsApi.getFlowSnapshot(sessionId, undefined),
        ),
      { revalidate: false, populateCache: true },
    );
  }, [sessionId]);

  const activePoll = shouldPoll(query.data as AudienceCreationFlowSnapshot | null | undefined);
  const { notifyActivity } = useRealtimeRepair({
    active: activePoll,
    onRepair: (reason) => {
      incrementPollWonRepair('IMPORT', reason);
      void revalidateSnapshot().catch(() => undefined);
    },
  });

  useEffect(() => {
    notifyActivity();
  }, [notifyActivity, query.dataUpdatedAt]);

  const snap = query.data as AudienceCreationFlowSnapshot | null | undefined;
  const hasPreflightData = snap?.hasPreflightData;
  const wizardPhase = snap?.wizardPhase;
  const awaitingPreflightPollRef = useRef(false);
  useEffect(() => {
    if (!sessionId) {
      awaitingPreflightPollRef.current = false;
      return;
    }
    const awaiting =
      hasPreflightData === false &&
      (wizardPhase === "identity_review" || wizardPhase === "segmentation");
    if (awaiting && !awaitingPreflightPollRef.current) {
      void revalidateSnapshot();
    }
    awaitingPreflightPollRef.current = awaiting;
  }, [sessionId, hasPreflightData, wizardPhase, revalidateSnapshot]);

  masterHandlerRef.current = async (event: ImportSessionUpdatedEvent) => {
    notifyActivity();
    if (!shouldRevalidateFlowSnapshotForImportSessionUpdate(event)) {
      handlersRef.current.forEach((h) => void h(event));
      return;
    }
    const ack = getRouteAckFromImportSessionEvent(event);
    const currentVersion =
      (query.data as AudienceCreationFlowSnapshot | null | undefined)
        ?.machineVersion ?? 0;
    if (ack?.flowSnapshot != null && typeof ack.flowSnapshot === "object") {
      if (
        typeof ack.machineVersion === "number" &&
        ack.machineVersion < currentVersion
      ) {
        handlersRef.current.forEach((h) => void h(event));
        return;
      }
      await query.mutate(
        (current) =>
          preferNewerFlowSnapshot(
            current as AudienceCreationFlowSnapshot | null | undefined,
            ack.flowSnapshot as AudienceCreationFlowSnapshot,
          ),
        { revalidate: false },
      );
      handlersRef.current.forEach((h) => void h(event));
      return;
    }
    await revalidateSnapshot().catch(() => undefined);
    handlersRef.current.forEach((h) => void h(event));
  };

  return {
    snapshot: query.data ?? null,
    error: query.error as Error | undefined,
    isLoading: query.isLoading,
    isValidating: query.isValidating,
    mutate: query.mutate,
    isRealtimeConnected,
    registerImportSessionEventHandler: registerHandler,
  };
}
